const ACTIVE_TRANSLATION_STATUSES = new Set([
  'pending',
  'queued',
  'launching',
  'running',
  'processing',
  'in-progress',
  'retrying',
]);

export type TranslatedContentHydrationReason =
  | 'background_hydration'
  | 'initial_view'
  | 'job_completion'
  | 'language_switch'
  | 'manual_refresh'
  | 'proofread_preflight';

export interface ChapterTranslationStatus {
  bookId?: unknown;
  chapterGuid?: unknown;
  sourceLang?: unknown;
  targetLang?: unknown;
  type?: unknown;
  status?: unknown;
  processingState?: unknown;
}

interface ChapterTranslationIdentity {
  bookId: string;
  chapterGuid: string;
  sourceLang: string;
  targetLang: string;
}

const normalizeIdentity = (value: unknown): string =>
  String(value || '').replace(/0/g, ' ').trim().toLowerCase();

const languageMatches = (actual: unknown, expected: string): boolean => {
  const normalizedActual = normalizeIdentity(actual);
  const normalizedExpected = normalizeIdentity(expected);
  if (!normalizedActual || !normalizedExpected) return false;
  return normalizedActual === normalizedExpected
    || normalizedActual.endsWith(`-${normalizedExpected}`)
    || normalizedActual.endsWith(` to ${normalizedExpected}`);
};

export const isActiveTranslationStatus = (
  status?: unknown,
  processingState?: unknown,
): boolean => {
  const normalized = String(status || processingState || '').trim().toLowerCase();
  return ACTIVE_TRANSLATION_STATUSES.has(normalized);
};

export const isTranslationForChapterPair = (
  translation: ChapterTranslationStatus,
  identity: ChapterTranslationIdentity,
): boolean => {
  if (normalizeIdentity(translation.type) !== 'translation') return false;
  const translationBookId = normalizeIdentity(translation.bookId);
  if (translationBookId && translationBookId !== normalizeIdentity(identity.bookId)) return false;
  if (normalizeIdentity(translation.chapterGuid) !== normalizeIdentity(identity.chapterGuid)) return false;
  if (!languageMatches(translation.sourceLang, identity.sourceLang)) return false;
  return languageMatches(translation.targetLang, identity.targetLang);
};

export const isChapterTranslationActive = (
  translations: ChapterTranslationStatus[],
  identity: ChapterTranslationIdentity,
): boolean => translations.some(translation => (
  isTranslationForChapterPair(translation, identity)
  && isActiveTranslationStatus(translation.status, translation.processingState)
));

export const translatedContentBackoffMs = (attempt: number): number => {
  const boundedAttempt = Math.max(1, Math.min(6, Math.trunc(attempt || 1)));
  return Math.min(5 * 60_000, 15_000 * (2 ** (boundedAttempt - 1)));
};

interface HydrationRequestOptions<T> {
  key: string;
  isActive: boolean;
  invalidateNegativeCache?: boolean;
  load: () => Promise<T>;
  isMissingError: (error: unknown) => boolean;
  isMissingResult?: (result: T) => boolean;
  expectReady?: boolean;
  isCurrent?: () => boolean;
}

export class TranslatedContentHydrationCoordinator {
  private readonly inFlight = new Map<string, Promise<unknown | null>>();

  private readonly expectingReady = new Set<string>();

  private readonly negativeCache = new Map<string, { attempts: number; retryAt: number }>();

  constructor(private readonly now: () => number = () => Date.now()) {}

  invalidate(key: string): void {
    this.negativeCache.delete(key);
  }

  request<T>({
    key,
    isActive,
    invalidateNegativeCache = false,
    load,
    isMissingError,
    isMissingResult = result => result == null,
    expectReady = false,
    isCurrent = () => true,
  }: HydrationRequestOptions<T>): Promise<T | null> {
    const pending = this.inFlight.get(key);
    if (pending) {
      if (!expectReady || this.expectingReady.has(key)) return pending as Promise<T | null>;
      return pending.then(() => this.request({
        key, isActive, load, isMissingError, isMissingResult, expectReady, isCurrent, invalidateNegativeCache: true,
      }));
    }
    if (isActive) return Promise.resolve(null);

    if (invalidateNegativeCache || expectReady) this.negativeCache.delete(key);
    const retryState = this.negativeCache.get(key);
    if (retryState && this.now() < retryState.retryAt) return Promise.resolve(null);

    const rememberMissing = () => {
      const attempts = Math.min(6, (retryState?.attempts || 0) + 1);
      this.negativeCache.delete(key);
      this.negativeCache.set(key, { attempts, retryAt: this.now() + translatedContentBackoffMs(attempts) });
      if (this.negativeCache.size > 4096) this.negativeCache.delete(this.negativeCache.keys().next().value!);
    };
    const request = (async () => {
      // Completion can arrive before a UI projection catches up. Recheck a
      // bounded number of times, then surface a real missing-output problem.
      for (let attempt = 0; attempt < (expectReady ? 3 : 1); attempt += 1) {
        if (!isCurrent()) return null;
        let result: T | null;
        try { result = await load(); }
        catch (error) {
          if (!isMissingError(error)) throw error;
          result = null;
        }
        if (!isCurrent()) return null;
        if (result !== null && !isMissingResult(result)) {
          this.negativeCache.delete(key);
          return result;
        }
        if (expectReady && attempt < 2) await new Promise(resolve => setTimeout(resolve, 250 * (attempt + 1)));
      }
      rememberMissing();
      if (expectReady) throw new TranslationOutputMissingError();
      return null;
    })().finally(() => {
      this.inFlight.delete(key);
      this.expectingReady.delete(key);
    });

    if (expectReady) this.expectingReady.add(key);
    this.inFlight.set(key, request);
    return request;
  }
}

export class TranslationOutputMissingError extends Error {
  constructor() {
    super('Translation finished, but its saved chapter is unavailable. Refresh the book or contact support.');
    this.name = 'TranslationOutputMissingError';
  }
}
